import { useEffect, useRef, useState } from 'react'

/**
 * The element's current width, for an SVG that must size itself.
 *
 * An SVG stretched with `width: 100%` and a fixed viewBox scales its text
 * along with its marks, so a wide chart gets fat labels and a narrow one
 * gets unreadable ones. Measuring instead means the type stays the size it
 * was designed at whatever the column does.
 */
export function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0
      // Sub-pixel jitter from a scrollbar appearing would otherwise loop.
      setWidth(prev => (Math.abs(prev - w) > 1 ? w : prev))
    })
    ro.observe(el)
    setWidth(el.getBoundingClientRect().width)
    return () => ro.disconnect()
  }, [])

  return { ref, width }
}
